import { Button, Drawer } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorBoundaryWrapper } from '~/components/error-boundary-wrapper/ErrorBoundaryWrapper';
import { SendAllAssetsTo } from '~/components/send-all-assets-to/SendAllAssetsTo';
import { WithFooterWrapper } from '~/components/with-footer-wrapper/WithFooterWrapper';
import { useNavigateToAssetOverview } from '~/hooks/use-navigate-to-asset-overview/useNavigateToAssetOverview';
import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { queryKeys } from '~/routes/app-paths';

import { useCompetitorsData } from '../../hooks/useCompetitorsData';
import { CompetitorsTable } from './competitors-table/CompetitorsTable';
import { CompetitorsWidget } from './competitors-widget/CompetitorsWidget';
import * as S from './CompetitorsTab.styles';

export const DRAWER_COMPETITORS = 'competitors';

interface CompetitorsTabProps {
  entity: Entity;
}

export const CompetitorsTab = ({ entity }: CompetitorsTabProps) => {
  const { t } = useTranslation(['widget']);
  const { params, deleteQueryParam } = useQueryParams();
  const { navigateToPage } = useNavigateToAssetOverview();

  const competitorTag = params[queryKeys.competitorTag];

  const isDrawerOpen = params[queryKeys.activeDrawer] === DRAWER_COMPETITORS;

  const closeDrawer = () => {
    deleteQueryParam([
      queryKeys.activeDrawer,
      queryKeys.activeDrawerTab,
      queryKeys.competitorTag,
    ]);
  };

  const {
    tableData,
    columnsMeta,
    selectedCompetitor,
    competitorsByTag,
    isError,
    isFetching,
  } = useCompetitorsData({
    entityTag: entity.tag,
  });

  const displayedEntityTags = tableData
    .filter(row => row.hasData)
    .map(row => row.entity?.tag) as string[];

  return (
    <ErrorBoundaryWrapper>
      {!!displayedEntityTags.length && (
        <S.MenuBar>
          <S.AssetsCounter>
            {t(
              'widget:assetOverview.knowledgeGraph.competitors.assetsCounter',
              {
                count: displayedEntityTags.length,
              }
            )}
          </S.AssetsCounter>
          <SendAllAssetsTo
            entityTags={displayedEntityTags}
            source="asset_overview"
          />
        </S.MenuBar>
      )}
      <S.ScrollContainer>
        <CompetitorsTable
          tableData={tableData}
          columnsMeta={columnsMeta}
          isError={isError}
          isFetching={isFetching}
        />
      </S.ScrollContainer>
      <Drawer
        data-testid="competitors-drawer"
        isOpen={isDrawerOpen}
        handleCloseClick={closeDrawer}
        headerButton={
          competitorTag ? (
            <Button
              variant="empty"
              size="xsmall"
              label={t('themeOverview.drawer.assetOverview')}
              iconName="Expand"
              onClick={() => navigateToPage({ entityTag: competitorTag })}
            />
          ) : null
        }
      >
        {selectedCompetitor && (
          <WithFooterWrapper isWrappedComponentOpened={isDrawerOpen}>
            <CompetitorsWidget
              entity={entity}
              competitorData={competitorsByTag[selectedCompetitor]}
            />
          </WithFooterWrapper>
        )}
      </Drawer>
    </ErrorBoundaryWrapper>
  );
};
